import { z } from "zod";
import { Architecture } from "./catalogue.js";
import { Operation } from "./operations.js";
import { Labels } from "./ssh-keys.js";

/** Server states as providers report them; `unknown` covers anything new. */
export const ServerStatus = z.enum([
  "initializing",
  "starting",
  "running",
  "stopping",
  "off",
  "rebuilding",
  "migrating",
  "deleting",
  "unknown",
]);
export type ServerStatus = z.infer<typeof ServerStatus>;

export const Server = z.object({
  id: z.uuid(),
  name: z.string(),
  status: ServerStatus,
  type: z.object({
    code: z.string(),
    cores: z.number().int(),
    memoryGb: z.number(),
    diskGb: z.number(),
    architecture: z.enum(["x86", "arm"]),
    cpuKind: z.enum(["shared", "dedicated"]),
  }),
  region: z.string(),
  zone: z.string(),
  /** The image the server was created or last rebuilt from; null when it was deleted since. */
  image: z
    .object({ description: z.string(), osFlavor: z.string(), osVersion: z.string().nullable() })
    .nullable(),
  publicIpv4: z.object({ address: z.string(), reverseDns: z.string().nullable() }).nullable(),
  /** The /64 network routed to the server. */
  publicIpv6: z.object({ network: z.string() }).nullable(),
  privateIps: z.array(z.object({ networkRef: z.string(), ip: z.string() })),
  firewallRefs: z.array(z.string()),
  labels: Labels,
  protection: z.object({ delete: z.boolean(), rebuild: z.boolean() }),
  rescueEnabled: z.boolean(),
  /** The provider refuses other actions while it is busy with one. */
  locked: z.boolean(),
  /** Daily backup window (e.g. `22-02`), null when backups are off. */
  backupWindow: z.string().nullable(),
  isoMounted: z.string().nullable(),
  traffic: z.object({
    includedBytes: z.number(),
    outgoingBytes: z.number().nullable(),
    ingoingBytes: z.number().nullable(),
  }),
  createdAt: z.iso.datetime(),
});
export type Server = z.infer<typeof Server>;

/** Server names as the provider accepts them: a hostname, at most 63 characters. */
export const ServerName = z
  .string()
  .trim()
  .min(1)
  .max(63)
  .regex(/^[a-zA-Z0-9]([a-zA-Z0-9.-]*[a-zA-Z0-9])?$/, "Letters, digits, dots and hyphens");

export const UpdateServerRequest = z
  .object({ name: ServerName.optional(), labels: Labels.optional() })
  .refine((v) => v.name !== undefined || v.labels !== undefined, "Nothing to change");
export type UpdateServerRequest = z.infer<typeof UpdateServerRequest>;

/**
 * Body of `DELETE /servers/:id` (optional). The server's public IPs with
 * auto-delete on go with it unless they are named here: CloudServer turns
 * their auto-delete off first, so they stay in the project, unassigned.
 */
export const DeleteServerRequest = z.object({
  /** Our ids of public IPs assigned to this server that must outlive it. */
  keepPublicIpIds: z.array(z.uuid()).max(10).default([]),
});
export type DeleteServerRequest = z.input<typeof DeleteServerRequest>;

/** Actions of `POST /servers/:id/actions/:action`, named as in our API (SPEC §7.2). */
export const ServerAction = z.enum([
  "power-on",
  "shutdown",
  "power-off",
  "reboot",
  "reset",
  "enable-rescue",
  "disable-rescue",
  "reset-password",
  "rebuild",
  "change-type",
  "enable-backups",
  "disable-backups",
  "create-snapshot",
  "change-protection",
  "attach-iso",
  "detach-iso",
]);
export type ServerAction = z.infer<typeof ServerAction>;

export const EnableRescueRequest = z.object({
  /** Our ids of the project's SSH keys that may sign in to the rescue system. */
  sshKeyIds: z.array(z.uuid()).max(20).default([]),
});
export type EnableRescueRequest = z.infer<typeof EnableRescueRequest>;

export const RebuildServerRequest = z
  .object({
    /** Catalogue image code, e.g. `ubuntu-26.04`. */
    image: z.string().trim().min(1).max(128).optional(),
    /** Our id of a snapshot or backup: restores the server from it. */
    imageId: z.uuid().optional(),
  })
  .refine((v) => (v.image === undefined) !== (v.imageId === undefined), {
    message: "Give either a catalogue image or a snapshot or backup",
    path: ["image"],
  });
export type RebuildServerRequest = z.infer<typeof RebuildServerRequest>;

export const ChangeServerTypeRequest = z.object({
  /** Catalogue server type code, e.g. `cx33`. */
  type: z.string().trim().min(1).max(64),
  /** Grow the disk too; a server with a grown disk can never move back to a smaller type. */
  upgradeDisk: z.boolean(),
});
export type ChangeServerTypeRequest = z.infer<typeof ChangeServerTypeRequest>;

export const CreateSnapshotRequest = z.object({
  description: z.string().trim().max(255).default(""),
});
export type CreateSnapshotRequest = z.infer<typeof CreateSnapshotRequest>;

/**
 * An ISO image a server can boot from: an installer, a live system or a
 * driver disk. Public ones come from the provider; private ones are the
 * project's own (uploaded through the provider's support).
 */
export const Iso = z.object({
  /** The provider's id; what attaching takes. */
  ref: z.string(),
  /** Stable name of a public ISO; null for a private one. */
  name: z.string().nullable(),
  description: z.string(),
  kind: z.enum(["public", "private"]),
  /** Null when it works on any architecture. */
  architecture: Architecture.nullable(),
  /** When the provider stops offering it; null while it is not deprecated. */
  unavailableAfter: z.iso.datetime().nullable(),
});
export type Iso = z.infer<typeof Iso>;

export const AttachIsoRequest = z.object({
  /** `Iso.ref` of one of the ISOs listed for the server. */
  isoRef: z.string().trim().min(1).max(64),
});
export type AttachIsoRequest = z.infer<typeof AttachIsoRequest>;

export const ChangeProtectionRequest = z
  .object({ delete: z.boolean().optional(), rebuild: z.boolean().optional() })
  .refine((v) => v.delete !== undefined || v.rebuild !== undefined, "Nothing to change");
export type ChangeProtectionRequest = z.infer<typeof ChangeProtectionRequest>;

/**
 * Answer of an action or a delete (202): the server as it was when the change
 * started, the operation that follows it, and — for rescue, password reset and
 * rebuild — the new root password. The password is shown once: CloudServer
 * never stores or logs it.
 */
export const ServerActionResponse = z.object({
  server: Server,
  operation: Operation,
  rootPassword: z.string().nullable(),
});
export type ServerActionResponse = z.infer<typeof ServerActionResponse>;

export const MetricsKind = z.enum(["cpu", "disk", "network"]);
export type MetricsKind = z.infer<typeof MetricsKind>;

/** Units of a metrics series; the UI formats values by them. */
export const MetricsUnit = z.enum(["percent", "iops", "bytes_per_second", "packets_per_second"]);
export type MetricsUnit = z.infer<typeof MetricsUnit>;

/**
 * One series. Names per kind: cpu → `cpu`; disk → `iops.read`, `iops.write`,
 * `bandwidth.read`, `bandwidth.write`; network → `pps.in`, `pps.out`,
 * `bandwidth.in`, `bandwidth.out`.
 */
export const MetricsSeries = z.object({
  name: z.string(),
  unit: MetricsUnit,
  /** `[unix seconds, value]`; value is null where the provider has no sample. */
  points: z.array(z.tuple([z.number(), z.number().nullable()])),
});
export type MetricsSeries = z.infer<typeof MetricsSeries>;

export const ServerMetrics = z.object({
  kind: MetricsKind,
  from: z.iso.datetime(),
  to: z.iso.datetime(),
  stepSeconds: z.number().int().positive(),
  series: z.array(MetricsSeries),
});
export type ServerMetrics = z.infer<typeof ServerMetrics>;

/** At most 31 days per request; the step is chosen so a chart gets about this many points. */
export const METRICS_MAX_RANGE_MS = 31 * 24 * 60 * 60 * 1000;
export const METRICS_POINTS = 200;

export const ServerMetricsQuery = z
  .object({ kind: MetricsKind, from: z.iso.datetime(), to: z.iso.datetime() })
  .refine((q) => Date.parse(q.from) < Date.parse(q.to), {
    message: "from must be before to",
    path: ["from"],
  })
  .refine((q) => Date.parse(q.to) - Date.parse(q.from) <= METRICS_MAX_RANGE_MS, {
    message: "At most 31 days",
    path: ["from"],
  });
export type ServerMetricsQuery = z.infer<typeof ServerMetricsQuery>;
